import { useState, useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { message } from "antd";
import { AdvancedImage, placeholder } from "@cloudinary/react";
import { thumbnail } from "@cloudinary/url-gen/actions/resize";
import { XMarkIcon, ArrowUpIcon } from "@heroicons/react/24/solid";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import {
  cld,
  handleFileUpload,
  MAX_IMAGE_SIZE_MB,
} from "../../../utils/cloudinary";

interface ImageUploadInputProps {
  name: string,
  readOnly?: boolean,
  required?: boolean,
  minImageSize?: { width: number; height: number },
  recommendedAspectRatio?: string,
}
const ImageUploadInput: React.FC<ImageUploadInputProps> = ({ name, readOnly = false, required = false, minImageSize, recommendedAspectRatio }) => {
  const { register, setValue, watch } = useFormContext();

  const [uploading, setUploading] = useState(false);
  const onDropAccepted = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      setUploading(true);
      const fileInfo = await handleFileUpload(file);
      setValue(name, fileInfo.public_id, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setUploading(false);
    },
    [name, setValue]
  );
  
  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      if (fileRejections[0].errors[0].code === ErrorCode.FileTooLarge) {
        message.warning('File is larger than 10MB.')
      }
    } 
  , [])

  const { getRootProps, getInputProps, isDragActive, isDragAccept } =
    useDropzone({
      accept: { "image/*": [] },
      multiple: false,
      disabled: readOnly,
      maxSize: MAX_IMAGE_SIZE_MB * 1024 * 1024,
      onDropAccepted,
      onDropRejected
    });

  const imageId = watch(name);

  const image = useMemo(() => imageId ?
    <AdvancedImage cldImg={cld.image(imageId).resize(thumbnail().height(140)).format("png")} plugins={[placeholder()]} />
    : null,
    [imageId])

  const removePicture = function () {
    setValue(name, null, { shouldDirty: true, shouldTouch: true });
  };

  return (
    <>
      <input type="hidden" {...register(name, { required })} />
      {!image && (
        <div
          className={
            "bg-white max-w-lg flex justify-center px-6 pt-5 pb-6 h-30 border-2 border-dashed rounded-md relative place-content-center " +
            (isDragActive
              ? isDragAccept
                ? "border-blue-600"
                : "border-red-600"
              : "border-gray-300")
          }
          {...getRootProps()}
        >
          {uploading && (
            <div className="place-content-center">
              <div className="animate-bounce bg-white dark:bg-slate-800 p-2 w-10 h-10 ring-1 ring-slate-900/5 dark:ring-slate-200/20 shadow-lg rounded-full flex items-center justify-center">
                <ArrowUpIcon className="w-6 h-6 text-violet-500" />
              </div>
              <p className="text-sm block">Uploading</p>
            </div>
          )}
          <div
            className={
              "space-y-1 text-center " + (uploading ? "blur-sm" : "")
            }
          >
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {!isDragActive && (
              <>
                <div className="flex text-sm text-gray-600">
                  <span className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                    <span>Upload a file</span>
                    <input className="sr-only" {...getInputProps()} />
                  </span>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF up to {MAX_IMAGE_SIZE_MB}MB
                </p>
                {minImageSize && (
                  <p className="text-xs text-gray-600 font-medium mt-1">
                    Minimum: {minImageSize.width}x{minImageSize.height}
                  </p>
                )}
                {recommendedAspectRatio && (
                  <p className="text-xs text-gray-600 font-medium">
                    Recommended aspect ratio: {recommendedAspectRatio}
                  </p>
                )}
              </>
            )}
            {isDragActive && <div className="flex text-sm">&nbsp;</div>}
            {isDragActive && isDragAccept && (
              <p className="text-xs text-green-600 font-medium">
                Drop file to upload
              </p>
            )}
            {isDragActive && !isDragAccept && (
              <p className="text-xs text-red-600 font-medium">
                PNG, JPG, GIF up to {MAX_IMAGE_SIZE_MB}MB
              </p>
            )}
          </div>
        </div>
      )}

      {image && (
        <div className="relative inline-flex">
          {image}
          <button
            type="button"
            className="absolute -top-2 -right-2 inline-flex items-center p-0 border border-transparent rounded-full shadow-sm text-white bg-gray-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={removePicture}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

    </>
  );
};

export default ImageUploadInput;
